/**
 * WeaveIn.ai Platform - Sponsor Applications API Service
 * TypeScript service layer for CBO application approval workflow
 */

import { getWeaveinApiUrl } from '@/src/config';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface CBOApplication {
  id: number;
  cbo_id: number;
  cbo_name: string;
  program_id: number;
  program_name: string;
  sponsor_id: number;
  match_score_at_application: number;
  application_date: string;
  application_status: string;
  application_message?: string;
  auto_approved: boolean;
  approval_threshold_met: boolean;
  // CBO details
  cbo_quality_score?: number;
  cbo_current_members?: number;
  cbo_capacity?: number;
  cbo_success_rate?: number;
  predicted_enrollments?: number;
  predicted_roi?: number;
}

export interface ApplicationApproval {
  approval_notes?: string;
  enrollment_start_date?: string;
}

export interface ApplicationRejection {
  rejection_reason: string;
}

export interface ApplicationAction {
  success: boolean;
  message: string;
  application_id: number;
}

export interface ApplicationStats {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

// ============================================================================
// API CLIENT
// ============================================================================

class SponsorApplicationsAPI {
  private getHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  /**
   * Get pending applications for review
   */
  async getPendingApplications(
    sponsorId: number,
    programId?: number
  ): Promise<CBOApplication[]> {
    const url = getWeaveinApiUrl('/api/sponsor/applications/pending');
    const params = new URLSearchParams({ sponsor_id: sponsorId.toString() });
    if (programId) {
      params.append('program_id', programId.toString());
    }

    const response = await fetch(`${url}?${params}`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch pending applications: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get application details
   */
  async getApplicationDetails(applicationId: number): Promise<CBOApplication> {
    const url = getWeaveinApiUrl(`/api/sponsor/applications/${applicationId}`);
    const response = await fetch(url, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch application details: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Approve an application
   */
  async approveApplication(
    applicationId: number,
    approval: ApplicationApproval
  ): Promise<ApplicationAction> {
    const url = getWeaveinApiUrl(`/api/sponsor/applications/${applicationId}/approve`);
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(approval)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to approve application: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Reject an application
   */
  async rejectApplication(
    applicationId: number,
    rejection: ApplicationRejection
  ): Promise<ApplicationAction> {
    const url = getWeaveinApiUrl(`/api/sponsor/applications/${applicationId}/reject`);
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(rejection)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to reject application: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get application statistics
   */
  async getApplicationStats(sponsorId: number): Promise<ApplicationStats> {
    const url = getWeaveinApiUrl('/api/sponsor/applications/stats');
    const params = new URLSearchParams({ sponsor_id: sponsorId.toString() });

    const response = await fetch(`${url}?${params}`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch application stats: ${response.status}`);
    }

    return response.json();
  }
}

// Export singleton instance
export const sponsorApplicationsAPI = new SponsorApplicationsAPI();
